<script lang="ts">
  import { useModalAnimation } from '../lib/useModalAnimation.svelte';

  interface Props {
    open: boolean;
    sourceProjectTitle: string;
    targetProjectTitle: string;
    taskCount: number;
    onCancel: () => void;
    onConfirm: () => void;
  }

  let { open, sourceProjectTitle, targetProjectTitle, taskCount, onCancel, onConfirm }: Props =
    $props();

  const modal = useModalAnimation(
    () => open,
    () => onCancel(),
  );

  function handleKeydown(e: KeyboardEvent) {
    if (!open) return;
    if (e.key === 'Escape') modal.animateClose();
    if (e.key === 'Enter') onConfirm();
  }
</script>

<svelte:window onkeydown={open ? handleKeydown : undefined} />

{#if open}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="modal-overlay"
    class:closing={modal.closing}
    bind:this={modal.overlayEl}
    onclick={modal.animateClose}
    role="presentation"
  >
    <div class="modal-card" onclick={(e) => e.stopPropagation()} role="dialog" tabindex="-1">
      <div class="modal-header">
        <div class="modal-icon">↗</div>
        <h2 class="modal-title">Move incomplete tasks?</h2>
      </div>
      <div class="modal-body">
        <p>
          Move <strong>{taskCount}</strong> incomplete {taskCount === 1 ? 'task' : 'tasks'} from
          <strong>{sourceProjectTitle}</strong> to <strong>{targetProjectTitle}</strong>?
        </p>
        <p class="modal-subtext">Completed tasks won’t be moved.</p>
      </div>
      <div class="modal-actions">
        <button type="button" class="btn btn-cancel" onclick={modal.animateClose}>Cancel</button>
        <button type="button" class="btn btn-confirm" onclick={onConfirm}>Move tasks</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 300;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.45);
    animation: overlay-in 150ms ease-out both;
  }

  .modal-overlay.closing {
    animation: overlay-out 100ms ease-in both;
  }

  .modal-overlay.closing .modal-card {
    animation: card-out 100ms ease-in both;
  }

  .modal-card {
    width: min(30rem, calc(100vw - 2rem));
    background: #fff;
    border-radius: 0.75rem;
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 8px 10px -6px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    animation: card-in 150ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  .modal-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem 1.1rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .modal-icon {
    color: #3b82f6;
    font-size: 1.1rem;
  }

  .modal-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #111827;
  }

  .modal-body {
    padding: 0.95rem 1.1rem;
    color: #1f2937;
    display: grid;
    gap: 0.45rem;
    line-height: 1.45;
  }

  .modal-subtext {
    margin: 0;
    color: #6b7280;
    font-size: 0.9rem;
  }

  .modal-actions {
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: flex-end;
    gap: 0.55rem;
    padding: 0.85rem 1.1rem;
  }

  .btn {
    border: 1px solid transparent;
    border-radius: 0.55rem;
    padding: 0.45rem 0.8rem;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
  }

  .btn-cancel {
    background: #fff;
    border-color: #d1d5db;
    color: #374151;
  }

  .btn-cancel:hover {
    background: #f9fafb;
  }

  .btn-confirm {
    background: #2563eb;
    color: #fff;
  }

  .btn-confirm:hover {
    background: #1d4ed8;
  }

  @keyframes overlay-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes overlay-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes card-in {
    from {
      transform: scale(0.96);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes card-out {
    from {
      transform: scale(1);
      opacity: 1;
    }
    to {
      transform: scale(0.96);
      opacity: 0;
    }
  }
</style>
